import React from 'react';
import { Box, Typography, Paper, useTheme } from '@mui/material';
import {
  TrendingUp,
  Rocket,
  EmojiEvents,
  Groups,
  Business,
} from '@mui/icons-material';

interface TimelineItem {
  year: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const CompanyTimeline: React.FC = () => {
  const theme = useTheme();

  const timeline: TimelineItem[] = [
    {
      year: '2010',
      title: 'The Beginning',
      description: 'VegaDigital founded by industry veterans',
      icon: <Rocket />,
      color: theme.palette.primary.main,
    },
    {
      year: '2013',
      title: 'Team Growth',
      description: 'Expanded to 10+ developers',
      icon: <Groups />,
      color: theme.palette.secondary.main,
    },
    {
      year: '2017',
      title: 'Enterprise Focus',
      description: 'Served first Fortune 500 client',
      icon: <Business />,
      color: theme.palette.success.main,
    },
    {
      year: '2021',
      title: 'Innovation',
      description: 'Launched AI-powered development tools',
      icon: <TrendingUp />,
      color: theme.palette.warning.main,
    },
    {
      year: '2025',
      title: 'Industry Leader',
      description: '100+ successful projects delivered',
      icon: <EmojiEvents />,
      color: '#FFD700',
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        minHeight: 300,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: 3,
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.palette.primary.main}10 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.palette.secondary.main}10 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Timeline items */}
      {timeline.map((item, index) => (
        <Box
          key={item.year}
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            animation: `slideIn 0.5s ease-out ${index * 0.2}s both`,
            '@keyframes slideIn': {
              from: {
                opacity: 0,
                transform: 'translateX(-20px)',
              },
              to: {
                opacity: 1,
                transform: 'translateX(0)',
              },
            },
          }}
        >
          {/* Icon */}
          <Paper
            elevation={3}
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: item.color,
              color: 'white',
              flexShrink: 0,
              zIndex: 2,
            }}
          >
            {item.icon}
          </Paper>

          {/* Content */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: item.color,
                  fontSize: '1.1rem',
                }}
              >
                {item.year}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '0.95rem',
                }}
              >
                {item.title}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.85rem',
              }}
            >
              {item.description}
            </Typography>
          </Box>

          {/* Connector line (except for last item) */}
          {index < timeline.length - 1 && (
            <Box
              sx={{
                position: 'absolute',
                left: 23,
                top: 48,
                width: 2,
                height: 'calc(100% + 8px)',
                bgcolor: item.color,
                opacity: 0.3,
                zIndex: 1,
              }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default CompanyTimeline;
